import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { CellMeasurerCache, List } from "react-virtualized";

import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import EventList from "@app/scene/dashboard/components/TimelineContent/components/EventList";
import TimelineEventType from "@app/scene/dashboard/enum/TimelineEventType";
import TimelineViewFilter from "@app/scene/dashboard/enum/TimelineViewFilter";
import { fetchTimelineEvents } from "@app/scene/dashboard/store/actions";
import { getEvents, getEventsFilters, isLoading } from "@app/scene/dashboard/store/selectors";
import { getTimelineEventsExpanded } from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import Scope from "@app/types/dashboard/Scope";
import TimelineEvent from "@app/types/events/TimelineEvent";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useSearchState from "@app/util/hook/useSearchState";

import FilterBar from "./components/FilterBar";
import LoadingStatusPanel from "./components/LoadingStatusPanel";
import styles from "./TimelineContent.module.scss";

const rowHeightCache = new CellMeasurerCache({
  defaultHeight: 100,
  fixedWidth: true,
});

interface TimelineContentProps {
  className?: string;
  scope: Scope;
}

interface Props extends TimelineContentProps {
  events: TimelineEvent[];
  eventsExpanded: boolean;
  eventTypes: TimelineEventType[];
  loading?: boolean;
  onFetchTimelineEvents: typeof fetchTimelineEvents.request;
  onUpdateMetadata: typeof updateMetadata.request;
}

const TimelineContent: React.FC<Props> = ({
  className,
  events,
  eventsExpanded,
  eventTypes,
  loading,
  onFetchTimelineEvents,
  onUpdateMetadata,
  scope,
}) => {
  const analytics = useExtendedAnalytics("Timeline", useAnalytics());

  const listRef = useRef<List>(null);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [query, setQuery] = useSearchState("");
  const [viewFilter, setViewFilter] = useState<TimelineViewFilter | undefined>();

  useChangeTracking(() => {
    analytics.clicked(["Quick filter pill"], { viewFilter });
  }, [viewFilter, analytics]);

  const handleClearFilters = useCallback(() => {
    setQuery("");
    setViewFilter(undefined);
    setStartDate(new Date());
  }, [setQuery]);

  useEffect(() => {
    onFetchTimelineEvents({
      onComplete: () => {
        rowHeightCache.clearAll();
        listRef.current?.scrollToPosition(0);
        listRef.current?.recomputeRowHeights(0);
      },
      query,
      scope,
      startDate,
      viewFilter,
    });
  }, [query, startDate, viewFilter, onFetchTimelineEvents, scope]);

  useEffect(() => {
    listRef.current?.recomputeRowHeights(0);
  }, [eventsExpanded]);

  const handleEventTypesChange = useCallback(
    (eventTypes: TimelineEventType[]) => {
      analytics.clicked(["Filter", "apply"], { eventTypes });
      onFetchTimelineEvents({
        eventTypes,
        onComplete: () => {
          rowHeightCache.clearAll();
          listRef.current?.scrollToPosition(0);
          listRef.current?.recomputeRowHeights(0);
        },
        scope,
      });
    },
    [onFetchTimelineEvents, scope, analytics]
  );

  const handleLoadMore = useCallback(
    () => onFetchTimelineEvents({ scope }),
    [onFetchTimelineEvents, scope]
  );

  const [eventTypesPopoverVisible, setEventTypesPopoverVisibility] = useState(false);
  useChangeTracking(() => analytics.clicked(["Filter"]), [analytics, eventTypesPopoverVisible]);

  const handleShowTimelinePreferences = useCallback(() => {
    setEventTypesPopoverVisibility(true);
  }, []);

  const handleEventsExpanded = useCallback(
    (expanded: boolean) => {
      onUpdateMetadata({ timelineEventsExpanded: expanded });
    },
    [onUpdateMetadata]
  );

  return (
    <AnalyticsContext.Provider value={analytics}>
      <div className={cn(styles.container, className)}>
        <FilterBar
          className={styles.filterBar}
          eventTypes={eventTypes}
          eventTypesPopoverOpen={eventTypesPopoverVisible}
          onEventTypesChange={handleEventTypesChange}
          onEventTypesPopoverOpenChange={setEventTypesPopoverVisibility}
          onQueryChange={setQuery}
          onSetEventsExpanded={handleEventsExpanded}
          onStartDateChange={setStartDate}
          onViewFilterChange={setViewFilter}
          query={query}
          startDate={startDate}
          viewFilter={viewFilter}
        />
        <ExtendedAnalytics keyPath="Row">
          <EventList
            className={styles.content}
            events={events}
            eventsExpanded={eventsExpanded}
            loading={loading}
            onClearFilters={handleClearFilters}
            onLoadMore={handleLoadMore}
            onShowTimelinePreferences={handleShowTimelinePreferences}
            ref={listRef}
            rowHeightCache={rowHeightCache}
          />
        </ExtendedAnalytics>
        <LoadingStatusPanel loading={loading} />
      </div>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  events: getEvents(state),
  eventsExpanded: getTimelineEventsExpanded(state),
  eventTypes: getEventsFilters(state).eventTypes,
  loading: isLoading(state),
});

const mapDispatchToProps = {
  onFetchTimelineEvents: fetchTimelineEvents.request,
  onUpdateMetadata: updateMetadata.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(TimelineContent);
